import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * LGPD-compliant Cookie Consent Banner.
 * Required by Lei Geral de Proteção de Dados (Art. 7º, I).
 * Shows on first visit and stores consent in a cookie (not localStorage).
 */
export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if consent was already given via cookie
    const consent = document.cookie.split(';').find(c => c.trim().startsWith('rifamaker_cookie_consent='));
    if (!consent) {
      // Small delay so it doesn't flash on load
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    // Set cookie for 1 year
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `rifamaker_cookie_consent=accepted; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    setVisible(false);
  };

  const handleRejectOptional = () => {
    // Accept only essential cookies
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `rifamaker_cookie_consent=essential_only; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10000,
      background: 'rgba(45, 52, 54, 0.97)',
      backdropFilter: 'blur(10px)',
      color: '#fff',
      padding: '1.25rem 1.5rem',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
      animation: 'slideUp 0.4s ease-out',
    }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1.5rem',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: '280px', fontSize: '0.88rem', lineHeight: 1.6 }}>
          <strong style={{ fontSize: '0.95rem' }}>{t('cookies.title')}</strong>
          <p style={{ margin: '0.3rem 0 0', opacity: 0.85 }}>
            {t('cookies.message')}{' '}
            <Link to="/privacy" style={{ color: '#00CEC9', textDecoration: 'underline' }}>
              {t('cookies.learnMore')}
            </Link>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
          <button
            onClick={handleRejectOptional}
            style={{
              padding: '0.6rem 1.2rem',
              background: 'transparent',
              color: '#b2bec3',
              border: '1.5px solid #636e72',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => e.target.style.borderColor = '#fff'}
            onMouseOut={(e) => e.target.style.borderColor = '#636e72'}
          >
            {t('cookies.essentialOnly')}
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: '0.6rem 1.5rem',
              background: 'linear-gradient(135deg, #00CEC9, #00B894)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.85rem',
              transition: 'all 0.2s',
            }}
          >
            {t('cookies.acceptAll')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
